import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Music Studio · Book-a-session modal.
 * Founder plain-ink 2026-08-13. Wires the "Book a session" CTA to a real
 * form that POSTs to /api/music-studio/book-session, which sends SendGrid
 * mail to the Founder and an ack to the buyer.
 */
export default function BookSessionModal({ open, onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [brief, setBrief] = useState("");
  const [preferredWindow, setPreferredWindow] = useState("");
  const [status, setStatus] = useState("idle"); // idle | sending | ok | err
  const [message, setMessage] = useState("");

  useEffect(() => {
    if (open) {
      setStatus("idle");
      setMessage("");
    }
  }, [open]);

  const submit = async (e) => {
    e.preventDefault();
    if (status === "sending") return;
    if (!name.trim() || !email.includes("@") || brief.trim().length < 8) {
      setStatus("err");
      setMessage("Name, valid email, and at least a sentence of brief required.");
      return;
    }
    setStatus("sending");
    setMessage("");
    try {
      const r = await fetch(`${API}/api/music-studio/book-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          brief: brief.trim(),
          preferred_window: preferredWindow.trim(),
        }),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setStatus("ok");
      setMessage(j.message || "Session request received.");
    } catch (err) {
      setStatus("err");
      setMessage("Something jammed on the wire. Reply to any Resilience email and we'll book you by hand.");
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          data-testid="book-session-modal"
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4"
          onClick={onClose}
        >
          <motion.div
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 12, scale: 0.98 }}
            transition={{ duration: 0.22, ease: [0.16, 1, 0.3, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-lg bg-[#0b0b0d] border border-[#ff5a00]/30 p-8"
          >
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              data-testid="book-session-close"
              className="absolute top-3 right-4 text-[#a1a1aa] hover:text-[#ff5a00] transition-colors text-lg"
            >
              ×
            </button>
            <p className="font-mono text-[10px] uppercase tracking-[0.42em] text-[#ff5a00] mb-2">
              Music Studio · Book a session
            </p>
            <h3 className="font-display text-2xl sm:text-3xl text-[#ededed] mb-1">
              Tell us what you're making.
            </h3>
            <p className="font-body text-sm text-[#a1a1aa] mb-6">
              A real reply lands in your inbox within one business day with two
              proposed session windows. No credit card until the brief is
              scoped and you say yes.
            </p>

            {status === "ok" ? (
              <div
                data-testid="book-session-success"
                className="border border-[#ff5a00]/40 bg-black p-5"
              >
                <p className="font-display text-lg text-[#ededed]">
                  {message}
                </p>
                <button
                  type="button"
                  onClick={onClose}
                  className="mt-5 font-mono text-[10px] uppercase tracking-[0.32em] text-[#ff5a00] hover:text-[#ff8f00]"
                >
                  Close ×
                </button>
              </div>
            ) : (
              <form onSubmit={submit} className="space-y-4">
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] block mb-1">
                    Your name
                  </span>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    data-testid="book-session-name"
                    required
                    maxLength={120}
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#ff5a00] px-3 py-2 text-[#ededed] font-body outline-none transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] block mb-1">
                    Email
                  </span>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    data-testid="book-session-email"
                    required
                    maxLength={180}
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#ff5a00] px-3 py-2 text-[#ededed] font-body outline-none transition-colors"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] block mb-1">
                    Brief — what are we making?
                  </span>
                  <textarea
                    value={brief}
                    onChange={(e) => setBrief(e.target.value)}
                    data-testid="book-session-brief"
                    required
                    rows={4}
                    maxLength={4000}
                    placeholder="Score for a 4-minute animated short; noir-jazz reference; solo trumpet plus quartet…"
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#ff5a00] px-3 py-2 text-[#ededed] font-body outline-none transition-colors resize-none"
                  />
                </label>
                <label className="block">
                  <span className="font-mono text-[10px] uppercase tracking-[0.32em] text-[#a1a1aa] block mb-1">
                    Preferred window (optional)
                  </span>
                  <input
                    type="text"
                    value={preferredWindow}
                    onChange={(e) => setPreferredWindow(e.target.value)}
                    data-testid="book-session-window"
                    placeholder="Weekday mornings ET, or a specific date"
                    maxLength={120}
                    className="w-full bg-[#050505] border border-white/10 focus:border-[#ff5a00] px-3 py-2 text-[#ededed] font-body outline-none transition-colors"
                  />
                </label>

                {status === "err" && (
                  <p
                    data-testid="book-session-error"
                    className="font-mono text-[10px] uppercase tracking-[0.28em] text-[#ff5a00]"
                  >
                    {message}
                  </p>
                )}

                <button
                  type="submit"
                  disabled={status === "sending"}
                  data-testid="book-session-submit"
                  className="w-full font-mono text-[10px] uppercase tracking-[0.32em] bg-[#ff5a00] text-[#050505] px-6 py-3 hover:bg-[#ff8f00] transition-colors disabled:opacity-60 disabled:cursor-wait"
                >
                  {status === "sending" ? "Sending…" : "Send session request →"}
                </button>
              </form>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
